import { useState, type ReactNode } from 'react';
import { C, DOC_TYPES } from '../../../../lib/design';
import { Ic, ICON_PATHS } from '../../../../lib/ui';
import { useI18n } from '../../../../i18n';
import { DOC_COLORS } from '../../../../config/documentStyles';
import type { FichaDocumento } from '../../../shared/services/api';

// ─── ITEM INTERFACES ───

export interface FichaItemBase {
  id: string;
  [key: string]: unknown;
}

export interface EntradaItem extends FichaItemBase {
  descripcion: string;
  procesoOrigenId?: string | null;
  orden?: number;
}

export interface SalidaItem extends FichaItemBase {
  descripcion: string;
  procesoDestinoId?: string | null;
  orden?: number;
}

export interface ReporteItem extends FichaItemBase {
  nombre: string;
  frecuencia?: string | null;
  destinatario?: string | null;
}

export interface RequisitoItem extends FichaItemBase {
  descripcion: string;
  tipo?: string | null;
}

export interface HerramientaItem extends FichaItemBase {
  nombre: string;
  codigo?: string | null;
  descripcion?: string | null;
  moduloVianet?: string | null;
}

export interface InvolucradoItem extends FichaItemBase {
  rolDescripcion?: string | null;
  puestoId?: string | null;
}

export interface IndicadorItem extends FichaItemBase {
  nombre: string;
  descripcion?: string | null;
  unidad?: string | null;
  valorObjetivo?: number | null;
  tipoCalculo: string;
}

// ─── HOOK RETURN TYPE ───

export interface FichaEditHook<T extends FichaItemBase> {
  items: T[];
  loading: boolean;
  error: string | null;
  crear: (data: Record<string, unknown>) => Promise<unknown>;
  actualizar: (id: string, data: Partial<T>) => Promise<unknown>;
  eliminar: (id: string) => Promise<unknown>;
  refetch: () => void;
}

// ─── SECTION COMPONENT ───

export function Section({
  icon,
  title,
  children,
  fullWidth,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
  fullWidth?: boolean;
}) {
  return (
    <div
      style={{
        background: 'white',
        borderRadius: 10,
        border: `1px solid ${C.border}`,
        marginBottom: 16,
        ...(fullWidth ? { gridColumn: '1 / -1' } : {}),
      }}
    >
      <div
        style={{
          padding: '14px 20px',
          borderBottom: `1px solid ${C.borderAlt}`,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            background: `${DOC_COLORS.h3}10`,
            color: DOC_COLORS.h3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </div>
        <span style={{ fontSize: 14, fontWeight: 700, color: C.azul1 }}>{title}</span>
      </div>
      <div style={{ padding: '16px 20px' }}>{children}</div>
    </div>
  );
}

// ─── FIELD COMPONENT ───

export function Field({
  label,
  value,
  editMode,
  onChange,
}: {
  label: string;
  value: ReactNode;
  editMode?: boolean;
  onChange?: (v: string) => void;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          color: '#7a7a9a',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {editMode && onChange ? (
        <input
          type="text"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: C.text,
            width: '100%',
            border: `1px solid ${C.border}`,
            borderRadius: 6,
            padding: '4px 8px',
            fontFamily: 'inherit',
            outline: 'none',
            background: '#FAFBFC',
          }}
        />
      ) : (
        <div style={{ fontSize: 14, fontWeight: 500, color: C.text }}>{value}</div>
      )}
    </div>
  );
}

// ─── DOC LINK ───

export function DocLink({ doc, navigate }: { doc: FichaDocumento; navigate: (p: string) => void }) {
  const { t } = useI18n();
  return (
    <div
      onClick={() => navigate(`/documento/${doc.codigo}`)}
      style={{
        padding: '7px 12px',
        background: `${DOC_COLORS.h2}06`,
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        cursor: 'pointer',
        marginBottom: 4,
      }}
    >
      <Ic d={ICON_PATHS.doc} s={14} c={DOC_COLORS.h2} />
      <span
        style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, color: DOC_COLORS.h2 }}
      >
        {doc.codigo}
      </span>
      <span style={{ fontSize: 13, color: C.text, flex: 1 }}>{doc.titulo}</span>
      <span
        style={{
          fontSize: 10,
          fontWeight: 600,
          padding: '2px 8px',
          borderRadius: 4,
          color: C.azul2,
          background: `${C.azul2}12`,
        }}
      >
        {(t.docTypes as Record<string, string>)[doc.tipoDocumental] ??
          DOC_TYPES[doc.tipoDocumental as import('../../../../types').TipoDocumental]?.label ??
          doc.tipoDocumental}
      </span>
    </div>
  );
}

// ─── EMPTY STATE ───

export function EmptyState({ message }: { message: string }) {
  return (
    <div style={{ fontSize: 13, color: C.textMut, fontStyle: 'italic', padding: '8px 0' }}>
      {message}
    </div>
  );
}

// ─── INLINE EDIT ───

export function InlineEdit({
  value,
  onSave,
  editMode,
  style,
  placeholder,
  type,
}: {
  value: string;
  onSave: (v: string) => void;
  editMode: boolean;
  style?: React.CSSProperties;
  placeholder?: string;
  type?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const baseStyle: React.CSSProperties = { fontSize: 13, color: C.text, flex: 1, ...style };

  if (!editMode) return <span style={baseStyle}>{value}</span>;

  if (!editing) {
    return (
      <span
        onClick={() => {
          setEditing(true);
          setDraft(value);
        }}
        style={{ ...baseStyle, cursor: 'pointer', borderBottom: '1px dashed #ccc' }}
      >
        {value || placeholder || '(vacio)'}
      </span>
    );
  }

  return (
    <input
      autoFocus
      type={type ?? 'text'}
      value={draft}
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        setEditing(false);
        if (draft !== value) onSave(draft);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') {
          setEditing(false);
          setDraft(value);
        }
      }}
      style={{
        fontSize: 13,
        fontFamily: 'inherit',
        color: C.text,
        border: `1px dashed ${C.celeste}`,
        borderRadius: 4,
        padding: '2px 6px',
        flex: 1,
        outline: 'none',
        background: '#FAFBFC',
        ...style,
      }}
    />
  );
}

// ─── INLINE SELECT ───

export function InlineSelect({
  value,
  options,
  onSave,
  editMode,
  style,
}: {
  value: string;
  options: { value: string; label: string }[];
  onSave: (v: string) => void;
  editMode: boolean;
  style?: React.CSSProperties;
}) {
  const baseStyle: React.CSSProperties = { fontSize: 11, color: C.textMut, ...style };

  if (!editMode) {
    const label = options.find((o) => o.value === value)?.label ?? value;
    return label ? (
      <span
        style={{
          ...baseStyle,
          padding: '1px 6px',
          borderRadius: 4,
          background: `${C.azul2}10`,
        }}
      >
        {label}
      </span>
    ) : null;
  }

  return (
    <select
      value={value}
      onChange={(e) => onSave(e.target.value)}
      style={{
        fontSize: 11,
        fontFamily: 'inherit',
        color: C.text,
        border: `1px dashed ${C.celeste}`,
        borderRadius: 4,
        padding: '2px 4px',
        outline: 'none',
        background: '#FAFBFC',
        cursor: 'pointer',
        ...style,
      }}
    >
      <option value="">--</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

// ─── ADD ITEM ROW ───

export function AddItemRow({
  sectionKey,
  addingSection,
  setAddingSection,
  newItemValue,
  setNewItemValue,
  onAdd,
  addLabel,
  variant,
}: {
  sectionKey: string;
  addingSection: string | null;
  setAddingSection: (v: string | null) => void;
  newItemValue: string;
  setNewItemValue: (v: string) => void;
  onAdd: () => void;
  addLabel: string;
  variant?: 'pill' | 'row';
}) {
  if (addingSection === sectionKey) {
    if (variant === 'pill') {
      return (
        <div style={{ display: 'inline-flex', gap: 4 }}>
          <input
            value={newItemValue}
            onChange={(e) => setNewItemValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onAdd()}
            placeholder={addLabel}
            style={{
              border: `1px solid ${C.border}`,
              borderRadius: 16,
              padding: '3px 10px',
              fontSize: 12,
              fontFamily: 'inherit',
              outline: 'none',
              width: 160,
            }}
            autoFocus
          />
          <button
            onClick={onAdd}
            style={{
              border: 'none',
              borderRadius: 16,
              padding: '3px 10px',
              background: C.azul2,
              color: 'white',
              cursor: 'pointer',
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            +
          </button>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
        <input
          value={newItemValue}
          onChange={(e) => setNewItemValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onAdd()}
          placeholder={addLabel}
          style={{
            flex: 1,
            border: `1px solid ${C.border}`,
            borderRadius: 6,
            padding: '4px 8px',
            fontSize: 12,
            fontFamily: 'inherit',
            outline: 'none',
          }}
          autoFocus
        />
        <button
          onClick={onAdd}
          style={{
            border: 'none',
            borderRadius: 6,
            padding: '4px 10px',
            background: C.azul2,
            color: 'white',
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          +
        </button>
        <button
          onClick={() => {
            setAddingSection(null);
            setNewItemValue('');
          }}
          style={{
            border: 'none',
            background: 'none',
            color: C.textMut,
            cursor: 'pointer',
            fontSize: 12,
          }}
        >
          x
        </button>
      </div>
    );
  }

  if (variant === 'pill') {
    return (
      <button
        onClick={() => {
          setAddingSection(sectionKey);
          setNewItemValue('');
        }}
        style={{
          border: `1px dashed ${DOC_COLORS.h2}30`,
          borderRadius: 20,
          padding: '3px 10px',
          background: 'transparent',
          color: DOC_COLORS.h2,
          cursor: 'pointer',
          fontSize: 12,
        }}
      >
        + {addLabel}
      </button>
    );
  }

  return (
    <button
      onClick={() => {
        setAddingSection(sectionKey);
        setNewItemValue('');
      }}
      style={{
        marginTop: 6,
        border: `1px dashed ${C.border}`,
        borderRadius: 6,
        padding: '4px 12px',
        background: 'transparent',
        color: C.azul2,
        cursor: 'pointer',
        fontSize: 11,
        fontWeight: 600,
        width: '100%',
      }}
    >
      + {addLabel}
    </button>
  );
}
